import React, {useState} from 'react';
import { createFlutterwavePayment, FLUTTERWAVE_CONFIG } from '../flutterwaveConfig';
import { firebase, firestore } from '../firebaseConfig';

const CheckoutForm = ({ amount, customer, onSuccess, onClose, items, productData, quantity = 1 }) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [customerData, setCustomerData] = useState({
    name: '',
    email: '',
    phone_number: '',
    ...customer
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Helper function to get product image
  const getProductImage = (item) => {
    if (item?.product_media && Array.isArray(item.product_media) && item.product_media.length > 0) {
      return item.product_media[0];
    }
    if (item?.product_image && Array.isArray(item.product_image) && item.product_image.length > 0) {
      return item.product_image[0];
    }
    if (item?.product_image && typeof item.product_image === 'string') {
      return item.product_image;
    }
    return "https://via.placeholder.com/150x150?text=No+Image";
  };

  // Helper function to get product name
  const getProductName = (item) => {
    return item?.product_name || item?.title || item?.name || "Unknown Product";
  };

  // Save payment details to Firestore
  const savePaymentToFirestore = async (paymentResponse, customerInfo) => {
    try {
      console.log('Saving payment to Firestore...', { paymentResponse, customerInfo });

      // Get user if logged in — not required, order saves regardless
      const currentUser = firebase.auth().currentUser;

      const txId = paymentResponse.transaction_id || paymentResponse.tx_ref || paymentResponse.flw_ref || `tx_${Date.now()}`;

      // Prevent duplicate saves for the same transaction
      const existing = await firestore
        .collection('veth_payments')
        .where('transactionId', '==', txId)
        .limit(1)
        .get();
      if (!existing.empty) {
        console.log('Order already saved, skipping duplicate.');
        return existing.docs[0].id;
      }

      // Determine if this is a cart purchase or single product purchase
      const isCartPurchase = items && items.length > 0;
      const isSingleProduct = productData && !isCartPurchase;

      console.log('Payment type:', { isCartPurchase, isSingleProduct, itemsCount: items?.length });

      // Build comprehensive order details
      let orderDetails = {
        // Payment Information
        amount: amount,
        currency: FLUTTERWAVE_CONFIG.currency,
        transactionId: txId,
        paymentMethod: 'Flutterwave',
        paymentStatus: 'Successful',
        orderStatus: 'pending',
        createdAt: new Date(),

        // Customer Information (userId is optional — null for guests)
        userId: currentUser?.uid || null,
        payeeName: customerInfo.name,
        payeeEmail: customerInfo.email,
        payeePhoneNumber: customerInfo.phone_number,

        // Full payment response for reference
        paymentResponse: paymentResponse,
      };

      if (isCartPurchase) {
        // Cart purchase - multiple items with full details
        const productsInCart = items.map((item) => ({
          id: item.id,
          name: getProductName(item),
          title: item.title || item.product_name || item.name,
          quantity: item.quantity || 1,
          price: item.price || item.cost || 0,
          image: getProductImage(item),
          product_image: item.product_image,
          product_media: item.product_media,
          description: item.description || item.product_description,
          // Include all other item properties
          ...item
        }));

        orderDetails = {
          ...orderDetails,
          orderType: 'cart',
          productPaidFor: items.map((item) => getProductName(item)).join(", "),
          product_image: items.map((item) => getProductImage(item)).join(", "),
          quantity: items.reduce((total, item) => total + (item.quantity || 1), 0),
          totalItems: items.length,
          productsInCart,
          items: items, // Full items array
        };
      } else if (isSingleProduct) {
        // Single product purchase with full details
        const productName = productData.product_name || productData.title || productData.name || "Product";
        const productImage = getProductImage(productData);
        
        orderDetails = {
          ...orderDetails,
          orderType: 'single',
          productPaidFor: productName,
          product_image: productImage,
          quantity: quantity,
          productId: productData.id,
          productData: productData, // Full product data
          productsInCart: [{
            id: productData.id,
            name: productName,
            title: productData.title || productData.product_name || productData.name,
            quantity: quantity,
            price: productData.price || productData.cost || amount,
            image: productImage,
            product_image: productData.product_image,
            product_media: productData.product_media,
            description: productData.description || productData.product_description,
            // Include all other product properties
            ...productData
          }],
        };
      } else {
        // Fallback for cases where no product data is provided
        orderDetails = {
          ...orderDetails,
          orderType: 'unknown',
          productPaidFor: "Payment",
          product_image: "",
          quantity: 1,
        };
      }

      // Save to veth_payments collection
      console.log('Saving order details:', orderDetails);
      const docRef = await firestore
        .collection("veth_payments")
        .add(orderDetails);

      console.log("Payment data saved successfully to veth_payments collection with ID:", docRef.id);
      
      // Clear any pending payment from localStorage
      localStorage.removeItem('pendingPayment');
      
      return docRef.id;
    } catch (error) {
      console.error("Error saving payment data to Firestore:", error);
      console.error("Error details:", {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      
      // Store in localStorage as backup
      const pendingPayment = {
        paymentResponse,
        customerInfo,
        amount,
        items,
        productData,
        quantity,
        timestamp: new Date().toISOString(),
        error: error.message
      };
      localStorage.setItem('pendingPayment', JSON.stringify(pendingPayment));
      
      // Don't throw error here to avoid disrupting the payment flow
    }
  };

  // Log a buy attempt to Firestore the moment the user clicks Pay
  const logBuyAttempt = async () => {
    try {
      const currentUser = firebase.auth().currentUser;
      const isCartPurchase = items && items.length > 0;

      const productName = isCartPurchase
        ? items.map(getProductName).join(', ')
        : productData
        ? getProductName(productData)
        : 'Product';

      const productImage = isCartPurchase
        ? items.map(getProductImage).filter(Boolean).join(', ')
        : productData
        ? getProductImage(productData)
        : '';

      await firestore.collection('BuyAttempts').add({
        timestamp: new Date(),
        createdAt: new Date(),
        amount: amount,
        currency: 'NGN',
        payeeName: customerData.name,
        payeeEmail: customerData.email,
        payeePhoneNumber: customerData.phone_number,
        productPaidFor: productName,
        product_image: productImage,
        quantity: isCartPurchase
          ? items.reduce((s, i) => s + (i.quantity || 1), 0)
          : quantity || 1,
        orderType: isCartPurchase ? 'cart' : productData ? 'single' : 'unknown',
        userId: currentUser?.uid || null,
        status: 'initiated',
      });
      console.log('✅ Buy attempt logged to BuyAttempts collection');
    } catch (err) {
      console.error('Failed to log buy attempt:', err.message);
      // Non-blocking — payment flow continues regardless
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setErrorMessage(null);

    try {
      // Validate customer data
      if (!customerData.name || !customerData.email || !customerData.phone_number) {
        throw new Error('Please fill in all required fields');
      }

      // Log the attempt immediately when user clicks Pay
      await logBuyAttempt();

      // Store ALL order data in localStorage before payment redirect
      const orderDataToStore = {
        amount: amount,
        customerData: {
          name: customerData.name,
          email: customerData.email,
          phone_number: customerData.phone_number,
        },
        items: items ? JSON.parse(JSON.stringify(items)) : null, // Deep copy all items
        productData: productData ? JSON.parse(JSON.stringify(productData)) : null, // Deep copy product
        quantity: quantity,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('currentOrder', JSON.stringify(orderDataToStore));
      console.log('✅ Order data stored in localStorage:', orderDataToStore);

      const paymentData = {
        amount: amount,
        currency: FLUTTERWAVE_CONFIG.currency,
        customer: {
          name: customerData.name,
          email: customerData.email,
          phone_number: customerData.phone_number,
        },
        customizations: {
          title: 'Complete Payment',
          description: 'Complete your purchase',
        },
        redirect_url: window.location.origin + '/payment-success',
        meta: {
          source: 'react-app',
          // Send order data to webhook
          orderType: items && items.length > 0 ? 'cart' : (productData ? 'single' : 'unknown'),
          items: items ? JSON.parse(JSON.stringify(items)) : null, // Deep copy for webhook
          productData: productData ? JSON.parse(JSON.stringify(productData)) : null, // Deep copy
          quantity: quantity || 1,
        }
      };

      const response = await createFlutterwavePayment(paymentData);
      
      console.log('Flutterwave payment response:', response);
      
      if (response && response.status === 'successful') {
        console.log('Payment successful, saving to Firestore...');
        // Save payment details to Firestore
        try {
          await savePaymentToFirestore(response, customerData);
          console.log('Payment saved successfully');
        } catch (saveError) {
          console.error('Error saving payment:', saveError);
          // Continue even if save fails
        }
        onSuccess && onSuccess(response);
      } else {
        console.warn('Payment response status is not successful:', response);
      }
    } catch (error) {
      console.error('Payment error:', error);
      setErrorMessage(error.message || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Customer Information Fields */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-amber-200 mb-2">
            Full Name *
          </label>
          <input
            type="text"
            name="name"
            value={customerData.name}
            onChange={handleInputChange}
            required
            className="w-full px-3 py-2 border border-gray-300 dark:border-amber-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 dark:bg-amber-900/20 dark:text-white"
            placeholder="Enter your full name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-amber-200 mb-2">
            Email Address *
          </label>
          <input
            type="email"
            name="email"
            value={customerData.email}
            onChange={handleInputChange}
            required
            className="w-full px-3 py-2 border border-gray-300 dark:border-amber-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 dark:bg-amber-900/20 dark:text-white"
            placeholder="Enter your email address"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-amber-200 mb-2">
            Phone Number *
          </label>
          <input
            type="tel"
            name="phone_number"
            value={customerData.phone_number}
            onChange={handleInputChange}
            required
            className="w-full px-3 py-2 border border-gray-300 dark:border-amber-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 dark:bg-amber-900/20 dark:text-white"
            placeholder="Enter your phone number"
          />
        </div>
      </div>

      {/* Payment Amount Display */}
      <div className="bg-gray-50 dark:bg-amber-900/20 p-4 rounded-lg">
        <div className="flex justify-between items-center">
          <span className="text-lg font-medium text-gray-900 dark:text-white">Total Amount:</span>
          <span className="text-xl font-bold text-amber-600 dark:text-amber-400">
            ₦{amount ? amount.toLocaleString() : '0'}
          </span>
        </div>
      </div>

      {/* Flutterwave Security Badge */}
      <div className='items-center justify-center flex mt-5'>
        <p className='text-sm text-gray-600 dark:text-amber-200'>Secured by</p>
        <img 
          className='w-40 ml-2' 
          src="https://upload.wikimedia.org/wikipedia/commons/9/9e/Flutterwave_Logo.png" 
          alt="Flutterwave" 
        />
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-amber-600 dark:bg-amber-700 text-white py-4 px-6 rounded-xl font-semibold hover:bg-amber-700 dark:hover:bg-amber-600 disabled:bg-gray-300 dark:disabled:bg-amber-800/50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            Processing...
          </>
        ) : (
          'Pay Now'
        )}
      </button>

      {/* Error Message */}
      {errorMessage && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <p className="text-red-600 dark:text-red-400 text-sm">{errorMessage}</p>
        </div>
      )}
    </form>
  );
};

export default CheckoutForm